import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useI18n } from "../i18n";
import { Card } from "@/components/ui/card";
import { eur } from "../helpers";
import TodayBriefCard from "../components/TodayBriefCard";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Wallet, TrendingUp, Banknote, Percent, BedDouble, Gauge, Trophy, AlertTriangle, Zap, Home, ShieldCheck, ShieldAlert, Landmark, Sparkles, WashingMachine, Wrench, LogIn, LogOut, Users, CheckCircle2, XCircle, MinusCircle, ArrowRight } from "lucide-react";

const COST_META = [
  ["canone", "Canoni locazione", Home, "/accounting"], ["condominio", "Spese condominiali", Home, "/accounting"],
  ["luce", "Luce", Zap, "/accounting"], ["gas", "Gas", Zap, "/accounting"], ["internet", "Internet", Zap, "/accounting"],
  ["manutenzioni", "Manutenzioni", Wrench, "/maintenance"], ["lavanderia", "Lavanderia", WashingMachine, "/accounting"],
  ["pulizie", "Pulizie", Sparkles, "/cleanings"], ["commercialista", "Commercialista", Landmark, "/accounting"],
  ["assicurazioni", "Assicurazioni", ShieldCheck, "/accounting"], ["banca", "Banca", Banknote, "/accounting"],
  ["tasse", "Tasse", Landmark, "/accounting"], ["cedolare", "Cedolare secca", Landmark, "/accounting"], ["iva", "IVA", Landmark, "/accounting"],
];

const HEALTH = {
  verde: { icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500", label: "VERDE" },
  giallo: { icon: MinusCircle, cls: "bg-amber-50 border-amber-200 text-amber-800", dot: "bg-amber-500", label: "GIALLO" },
  rosso: { icon: XCircle, cls: "bg-rose-50 border-rose-200 text-rose-700", dot: "bg-rose-500", label: "ROSSO" },
};

const SEC_LEVEL = {
  green: { cls: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500", text: "text-emerald-800", label: "Nessun evento critico", icon: ShieldCheck, iconCls: "text-emerald-600" },
  yellow: { cls: "bg-amber-50 border-amber-200", dot: "bg-amber-500", text: "text-amber-800", label: "Eventi da controllare", icon: ShieldAlert, iconCls: "text-amber-600" },
  red: { cls: "bg-rose-50 border-rose-200", dot: "bg-rose-500", text: "text-rose-800", label: "Eventi critici rilevati", icon: ShieldAlert, iconCls: "text-rose-600" },
};

function SecurityBadge({ navigate }) {
  const [sec, setSec] = useState(null);
  useEffect(() => { api.get("/audit-logs/security-alerts").then((r) => setSec(r.data)).catch(() => setSec(null)); }, []);
  if (!sec) return null;
  const lv = SEC_LEVEL[sec.level] || SEC_LEVEL.green;
  const Icon = lv.icon;
  return (
    <Card className={`border p-4 ${lv.cls}`} data-testid="security-badge" data-level={sec.level}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`flex h-9 w-9 items-center justify-center rounded-full bg-white/70`}><Icon size={20} className={lv.iconCls} /></span>
          <div>
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${lv.dot}`} />
              <span className={`font-head text-base font-semibold ${lv.text}`}>Sicurezza · {lv.label}</span>
            </div>
            <div className="text-xs text-slate-500">Monitoraggio eventi sensibili degli ultimi 7 giorni</div>
          </div>
        </div>
        <button onClick={() => navigate("/audit")} data-testid="security-badge-link"
          className={`flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50`}>
          Apri storico <ArrowRight size={14} />
        </button>
      </div>
      {sec.items.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="security-badge-items">
          {sec.items.map((it) => (
            <span key={it.type} data-testid={`security-item-${it.type}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${it.severity === "red" ? "border-rose-300 bg-white text-rose-700" : "border-amber-300 bg-white text-amber-700"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${it.severity === "red" ? "bg-rose-500" : "bg-amber-500"}`} />
              {it.label}: <b>{it.count}</b>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Executive() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const go = (path) => () => navigate(path);

  useEffect(() => { api.get("/executive/dashboard").then((r) => setD(r.data)).catch(() => setD(null)); }, []);

  if (!d) return <div className="p-8 text-sm text-slate-400" data-testid="executive-loading">…</div>;

  const h = HEALTH[d.health?.status] || HEALTH.giallo;
  const HIcon = h.icon;

  const triCard = (icon, label, obj, positive, path, tid) => {
    const Icon = icon;
    return (
      <Card role="button" data-testid={`exec-tri-${tid}`} onClick={go(path)} className="cursor-pointer border-slate-200 p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Icon size={16} className="text-primary" /> {label}</div>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["lblToday", "today"], ["lblMonth", "month"], ["lblYear", "year"]].map(([lk, k]) => {
            const v = obj[k];
            const tone = positive ? (v >= 0 ? "text-emerald-700" : "text-rose-600") : "text-slate-900";
            return (
              <div key={k}>
                <div className={`font-head text-lg font-semibold ${tone}`}>{eur(v)}</div>
                <div className="text-[11px] uppercase text-slate-400">{t(lk)}</div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  const kpi = (icon, label, value, tone, path, tid, sub) => {
    const Icon = icon;
    return (
      <Card role="button" data-testid={`exec-kpi-${tid}`} onClick={go(path)} className="flex cursor-pointer items-center gap-3 border-slate-200 p-4 transition-all hover:-translate-y-0.5 hover:shadow-md">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={18} /></div>
        <div className="min-w-0"><div className="truncate font-head text-lg font-semibold text-slate-900">{value}</div><div className="text-xs text-slate-500">{label}</div>{sub ? <div className="text-xs font-medium text-slate-600">{sub}</div> : null}</div>
      </Card>
    );
  };

  const op = d.operations;
  const todayStr = new Date(d.period.today).toLocaleDateString("it-IT", { day: "numeric", month: "long" });
  const brief = [
    { icon: LogIn, label: t("checkinsToday"), value: op.checkins_today, path: "/calendar" },
    { icon: LogOut, label: t("checkoutsToday"), value: op.checkouts_today, path: "/calendar" },
    { icon: Sparkles, label: t("cleaningsTodo"), value: op.cleanings_todo, path: "/cleanings" },
    { icon: Wrench, label: t("maintenanceOpen"), value: op.maintenance_open, path: "/maintenance" },
    { icon: Banknote, label: t("balancesToday"), value: eur(op.balances_today), path: "/payments" },
    { icon: AlertTriangle, label: t("missingDocs"), value: `${op.missing_documents} ${t("guestLabel")}`, path: "/bookings", warn: op.missing_documents > 0 },
    { icon: BedDouble, label: t("currentOccupancy"), value: `${op.current_occupancy}%`, path: "/calendar" },
    { icon: Wallet, label: t("expectedRevenueToday"), value: eur(op.revenue_today), path: "/accounting" },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="executive-page">
      <SecurityBadge navigate={navigate} />
      <TodayBriefCard />
      {/* Today briefing */}
      <Card className="border-slate-200 p-5" data-testid="today-briefing">
        <div className="mb-4 flex items-center gap-2 font-head text-base font-semibold text-slate-900">
          <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">{todayStr}</span>
          {t("todayBriefing")}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {brief.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} role="button" onClick={go(b.path)} data-testid={`brief-${i}`}
                className={`flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-slate-50 cursor-pointer ${b.warn ? "border-rose-200 bg-rose-50" : "border-slate-200"}`}>
                <Icon size={18} className={b.warn ? "text-rose-600" : "text-primary"} />
                <div className="min-w-0"><div className="truncate font-head text-lg font-semibold text-slate-900">{b.value}</div><div className="text-[11px] text-slate-500">{b.label}</div></div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Health banner */}
      <div data-testid="business-health" className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${h.cls}`}>
        <div className="flex items-center gap-3">
          <span className={`flex h-3 w-3 rounded-full ${h.dot} animate-pulse`} />
          <HIcon size={22} />
          <div>
            <div className="font-head text-base font-semibold">{t("businessHealth")}: {h.label}</div>
            <div className="text-xs opacity-80">{d.health?.reasons?.length ? d.health.reasons.join(" · ") : t("healthGood")} · {d.period.month_label}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs opacity-70">{t("liquidityCurrent")}</div>
          <div className={`font-head text-2xl font-bold ${d.liquidity_current >= 0 ? "" : "text-rose-600"}`}>{eur(d.liquidity_current)}</div>
        </div>
      </div>

      {/* Operational KPIs */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {kpi(LogIn, t("checkinsToday"), op.checkins_today, "bg-emerald-100 text-emerald-700", "/calendar", "checkins")}
        {kpi(LogOut, t("checkoutsToday"), op.checkouts_today, "bg-orange-100 text-orange-700", "/calendar", "checkouts")}
        {kpi(Users, t("guestsPresent"), op.guests_present, "bg-indigo-100 text-indigo-700", "/bookings", "guests-present")}
        {kpi(Sparkles, t("cleaningsTodo"), op.cleanings_todo, "bg-cyan-100 text-cyan-700", "/cleanings", "cleanings-todo")}
        {kpi(Wrench, t("maintenanceOpen"), op.maintenance_open, "bg-amber-100 text-amber-700", "/maintenance", "maintenance-open")}
      </div>

      {/* Revenue / Profit / Cash triple cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {triCard(Wallet, t("revenueKpi"), d.revenue, false, "/accounting", "revenue")}
        {triCard(TrendingUp, t("netProfitKpi"), d.net_profit, true, "/accounting", "net")}
        {triCard(Banknote, t("cashFlowKpi"), d.cash_flow, true, "/accounting", "cash")}
      </div>

      {/* Operational financial KPIs */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {kpi(BedDouble, t("occupancy"), `${d.kpis.occupancy}%`, "bg-cyan-100 text-cyan-700", "/reports", "occupancy")}
        {kpi(Gauge, "ADR", eur(d.kpis.adr), "bg-indigo-100 text-indigo-700", "/reports", "adr")}
        {kpi(TrendingUp, "RevPAR", eur(d.kpis.revpar), "bg-violet-100 text-violet-700", "/reports", "revpar")}
        {kpi(Percent, t("otaCommission"), eur(d.commissions.ota), "bg-amber-100 text-amber-700", "/accounting", "ota")}
        {kpi(Percent, t("stripeCommission"), eur(d.commissions.stripe), "bg-rose-100 text-rose-700", "/payments", "stripe")}
        {kpi(Trophy, t("mostProfitable"), d.most_profitable ? d.most_profitable.name : "—", "bg-emerald-100 text-emerald-700", "/accounting", "most", d.most_profitable ? eur(d.most_profitable.value) : "")}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Trend chart */}
        <Card className="border-slate-200 p-5" data-testid="exec-trend">
          <h3 className="mb-4 font-head font-semibold text-slate-900">{t("trend12")}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.trend} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" name={t("revenueKpi")} stroke="#1e293b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="profit" name={t("netProfitKpi")} stroke="#059669" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Profitability + forecast */}
        <Card className="border-slate-200 p-5" data-testid="exec-forecast">
          <h3 className="mb-4 font-head font-semibold text-slate-900">{t("forecastTitle")}</h3>
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-emerald-50 p-4">
              <div className="text-xs text-emerald-700">{t("monthEndForecast")}</div>
              <div className={`font-head text-2xl font-bold ${d.forecast.month_end_profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{eur(d.forecast.month_end_profit)}</div>
            </div>
            <div role="button" onClick={go("/accounting")} className="cursor-pointer rounded-lg bg-slate-50 p-4">
              <div className="flex items-center gap-1 text-xs text-slate-600"><AlertTriangle size={12} /> {t("lossMaking")}</div>
              <div className="font-head text-base font-semibold text-slate-900">{d.least_profitable ? d.least_profitable.name : "—"}</div>
              <div className="text-sm text-rose-600">{d.least_profitable ? eur(d.least_profitable.margin) : ""}</div>
            </div>
          </div>
          <div className="mb-2 text-xs font-medium text-slate-500">{t("liquidityForecast")}</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={d.forecast.liquidity.map((l) => ({ name: `${l.days}gg`, ...l }))} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => eur(v)} />
              <Bar dataKey="value" name={t("netCash")} fill="#1e293b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
            {d.forecast.liquidity.map((l) => (
              <div key={l.days} data-testid={`liquidity-${l.days}`} className="rounded-md border border-slate-200 p-2">
                <div className="font-semibold text-slate-700">{l.days} gg</div>
                <div className={l.value >= 0 ? "text-emerald-700" : "text-rose-600"}>{eur(l.value)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Cost items */}
      <Card className="border-slate-200 p-5" data-testid="exec-costs">
        <h3 className="mb-4 font-head font-semibold text-slate-900">{t("costsMonth")}</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
          {COST_META.map(([k, label, Icon, path]) => (
            <div key={k} role="button" onClick={go(path)} data-testid={`exec-cost-${k}`} className="cursor-pointer rounded-lg border border-slate-200 p-3 transition-colors hover:bg-slate-50">
              <div className="flex items-center gap-1 text-[11px] text-slate-500"><Icon size={12} /> {label}</div>
              <div className="mt-1 font-head font-semibold text-slate-900">{eur(d.cost_items[k])}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
